import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Sparkles, Loader2, Wifi, WifiOff, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useChatbot } from "@/hooks/use-chatbot";
import { getUserData } from "@/lib/api";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { useTranslation } from "react-i18next";

interface TeamInsightsChatProps {
  teamName: string;
  teamId: string;
  comparisonData?: any;
  members?: Array<{
    userId?: {
      _id: string;
      name: string;
      email: string;
    };
    role: "member" | "admin";
  }>;
}

export default function TeamInsightsChat({ teamName, teamId, comparisonData, members }: TeamInsightsChatProps) {
  const [chatInput, setChatInput] = useState("");
  const { toast } = useToast();
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const userData = getUserData();
  // `/auth/me` returns the MongoDB user document, which uses `_id`.
  // Login responses use `id`, so support both response shapes before
  // constructing the chatbot request payload.
  const currentUserId = String(
    userData?.id ||
    userData?._id ||
    userData?.user?.id ||
    userData?.user?._id ||
    ''
  );

  // Predefined questions with translations
  const predefinedQuestions = [
    t('teamInsights.question1'),
    t('teamInsights.question2'),
    t('teamInsights.question3'),
    t('teamInsights.question4'),
    t('teamInsights.question5'),
  ];

  // Initialize chatbot hook with WebSocket streaming
  const {
    messages,
    isLoading,
    isStreaming,
    isConnected,
    error,
    sendMessage,
    clearMessages,
    reconnect,
  } = useChatbot({
    groupId: teamId,
    memberId: currentUserId,
    useStreaming: true,
    autoConnect: true,
  });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Show toast on error
  useEffect(() => {
    if (error) {
      toast({
        title: t('common.error'),
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isLoading) return;
    
    await sendMessage(chatInput);
    setChatInput("");
  };

  const handlePredefinedQuestion = (question: string) => {
    sendMessage(question);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Card className="p-4 bg-card sm:p-6">
      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold sm:text-xl">{t('teamInsights.title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('teamInsights.subtitle', { teamName })}
            </p>
          </div>
        </div>
        
        {/* Connection Status & Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={isConnected ? "default" : "secondary"} className="gap-1">
            {isConnected ? (
              <>
                <Wifi className="w-3 h-3" />
                {t('teamInsights.connected')}
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3" />
                {t('teamInsights.offline')}
              </>
            )}
          </Badge>
          
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearMessages}
              className="h-8 px-2"
            >
              {t('teamInsights.clear')}
            </Button>
          )}
          
          {!isConnected && (
            <Button
              variant="outline"
              size="sm"
              onClick={reconnect}
              className="h-8 px-2 gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              {t('teamInsights.reconnect')}
            </Button>
          )}
        </div>
      </div>

      {/* Predefined Questions */}
      {messages.length === 0 && (
        <div className="mb-6">
          <p className="text-sm font-medium mb-3 text-muted-foreground">{t('teamInsights.quickQuestions')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {predefinedQuestions.map((question, index) => (
              <Button
                key={index}
                variant="outline"
                className="h-auto py-3 px-4 text-left justify-start whitespace-normal hover:bg-primary/5 hover:border-primary/50 hover:text-primary"
                onClick={() => handlePredefinedQuestion(question)}
                disabled={isLoading || !isConnected}
              >
                <MessageSquare className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{question}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <ScrollArea className="h-[400px] mb-4 rounded-lg border bg-muted/20 p-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              {t('teamInsights.startConversation')}
            </p>
            {!isConnected && (
              <div className="mt-4">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[92%] break-words rounded-lg p-3 sm:max-w-[80%] ${
                    message.role === 'user'
                      ? 'bg-teal-400 text-primary-foreground'
                      : 'bg-muted border border-border'
                  }`}
                >
                  {message.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">
                      {message.content || (message.streaming ? '...' : '')}
                    </p>
                  ) : (
                    <div className="text-sm">
                      {message.content ? (
                        <MarkdownRenderer content={message.content} />
                      ) : message.streaming ? (
                        <span className="text-muted-foreground">...</span>
                      ) : null}
                    </div>
                  )}
                  {message.streaming && (
                    <div className="mt-2 flex items-center gap-2 text-xs opacity-70">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>{t('teamInsights.streaming')}</span>
                    </div>
                  )}
                  <p className="text-xs opacity-70 mt-1">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && !isStreaming && (
              <div className="flex justify-start">
                <div className="max-w-[92%] rounded-lg p-3 bg-muted border border-border sm:max-w-[80%]">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <p className="text-sm text-muted-foreground">{t('teamInsights.thinking')}</p>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input Area */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('teamInsights.placeholder')}
          disabled={isLoading || !isConnected}
          className="min-w-0 flex-1"
        />
        <Button
          onClick={handleSendMessage}
          disabled={!chatInput.trim() || isLoading || !isConnected}
          className="w-full gap-2 sm:w-auto"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {t('teamInsights.send')}
        </Button>
      </div>

      {/* Show predefined questions in chat as well */}
      {messages.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs font-medium mb-2 text-muted-foreground">{t('teamInsights.suggestedQuestions')}</p>
          <div className="flex flex-wrap gap-2">
            {predefinedQuestions.map((question, index) => (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-3 text-xs hover:bg-primary/5 hover:text-primary"
                onClick={() => handlePredefinedQuestion(question)}
                disabled={isLoading || !isConnected}
              >
                {question}
              </Button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
